import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { financialService, paymentService } from '../services/financialService';
import { formatCurrency, formatNumber, formatDate } from '../utils/formatters';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Download, 
  CheckCircle, 
  XCircle, 
  Clock,
  BarChart3,
  PieChart,
  FileText,
  Filter,
  Search,
  Loader,
  AlertCircle,
  CreditCard,
  Shield
} from 'lucide-react';

const FinancialCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600',
    orange: 'bg-orange-50 border-orange-200 text-orange-600',
    red: 'bg-red-50 border-red-200 text-red-600'
  };

  return (
    <div className={`card ${colorClasses[color]} border-l-4 border-l-${color}-500`}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className="h-8 w-8" />
        </div>
        <div className="ml-4 flex-1">
          <h3 className="text-sm font-medium">{title}</h3>
          <p className="text-2xl font-bold mt-1">{value}</p>
          <div className="flex items-center justify-between mt-2">
            <p className="text-sm opacity-75">{subtitle}</p>
            {trend && (
              <div className={`flex items-center text-sm ${
                trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
              }`}>
                <TrendingUp className={`h-4 w-4 mr-1 ${
                  trend.direction === 'down' && 'rotate-180'
                }`} />
                {trend.value}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const WithdrawalRequestCard = ({ request, onApprove, onReject, loading }) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');

  // Calculate net amount after 20% commission
  const commission = request.amount * 0.20;
  const netAmount = request.amount - commission;

  const handleApproveWithPayment = async () => {
    if (!window.confirm(`Process ${request.tutor_name}'s withdrawal of ${formatCurrency(request.amount)} via ${request.payment_method?.toUpperCase()}?`)) {
      return;
    }

    setProcessingPayment(true);
    setPaymentStatus('Processing payment...');

    try {
      let paymentResult;

      // Process payment based on payment method
      if (request.payment_method === 'paypal') {
        paymentResult = await paymentService.paypal.initiatePayout(
          request.payment_details, // PayPal email
          netAmount,
          'USD',
          `Tutor payout for withdrawal #${request.id} - ${request.tutor_name}`
        );
      } else if (request.payment_method === 'paystack') {
        paymentResult = await paymentService.paystack.initiateTransfer(
          request.payment_details, // PayStack recipient code
          netAmount,
          'NGN',
          `Tutor payout for withdrawal #${request.id} - ${request.tutor_name}`
        );
      } else {
        throw new Error('Unsupported payment method');
      }

      if (paymentResult.success) {
        setPaymentStatus('Payment processed successfully!');
        
        // Update the withdrawal request status
        const approvalResult = await financialService.approveWithdrawal(request.id, {
          transaction_id: paymentResult.transaction_id,
          payout_batch_id: paymentResult.payout_batch_id || paymentResult.transfer_code,
          payment_processed: true
        });

        if (approvalResult.success) {
          onApprove(request.id);
          alert(`✅ Payment successful! Transaction ID: ${paymentResult.transaction_id}`);
        } else {
          throw new Error('Failed to update withdrawal status');
        }
      } else {
        throw new Error(paymentResult.message);
      }
    } catch (error) {
      console.error('❌ Payment processing error:', error);
      setPaymentStatus(`Payment failed: ${error.message}`);
      alert(`❌ Payment failed: ${error.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    onReject(request.id, rejectReason);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'paypal':
        return <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">P</div>;
      case 'paystack':
        return <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold">PS</div>;
      case 'bank':
        return <CreditCard className="h-5 w-5 text-gray-600" />;
      default:
        return <CreditCard className="h-5 w-5 text-gray-400" />;
    }
  };

  const getPaymentMethodName = (method) => {
    switch (method) {
      case 'paypal': return 'PayPal';
      case 'paystack': return 'PayStack';
      case 'bank': return 'Bank Transfer';
      default: return method || 'Unknown';
    }
  };

  return (
    <div className="card border-l-4 border-l-blue-500">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{request.tutor_name}</h3>
              <p className="text-sm text-gray-500">{request.tutor_email}</p>
            </div>
          </div>

          {/* Payment Method Display */}
          {request.payment_method && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2 text-sm">
                {getPaymentMethodIcon(request.payment_method)}
                <span className="text-gray-600">Payment Method:</span>
                <span className="font-medium">{getPaymentMethodName(request.payment_method)}</span>
                {request.payment_details && (
                  <span className="text-gray-500 text-xs">({request.payment_details})</span>
                )}
              </div>
            </div>
          )}

          {/* Financial Breakdown */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-500">Requested:</span>
              <p className="text-gray-900">{formatCurrency(request.amount)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Commission (20%):</span>
              <p className="text-red-600">-{formatCurrency(commission)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Net Payout:</span>
              <p className="text-green-600 font-semibold">{formatCurrency(netAmount)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500">Date:</span>
              <p className="text-gray-900">{formatDate(request.created_at)}</p>
            </div>
          </div>

          {/* Payment Status */}
          {processingPayment && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Loader className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm text-blue-700">{paymentStatus}</span>
              </div>
            </div>
          )}

          {request.notes && (
            <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">{request.notes}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 ml-4 min-w-[140px]">
          <button
            onClick={handleApproveWithPayment}
            disabled={loading || processingPayment}
            className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {processingPayment ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <span>{processingPayment ? 'Processing...' : 'Approve & Pay'}</span>
          </button>

          {!showRejectForm ? (
            <button
              onClick={() => setShowRejectForm(true)}
              disabled={loading || processingPayment}
              className="flex items-center space-x-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <XCircle className="h-4 w-4" />
              <span>Reject</span>
            </button>
          ) : (
            <div className="space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                rows="2"
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleReject}
                  disabled={loading || !rejectReason.trim()}
                  className="flex-1 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="flex-1 px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FinancialDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    timeframe: 'month'
  });

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [overviewResponse, requestsResponse] = await Promise.all([
        financialService.getFinancialOverview(filters.timeframe),
        financialService.getWithdrawalRequests(filters.status)
      ]);

      if (overviewResponse.success) {
        setOverview(overviewResponse.data);
      } else {
        setError(overviewResponse.message);
      }

      if (requestsResponse.success) {
        setWithdrawalRequests(requestsResponse.data);
      }

    } catch (err) {
      console.error('❌ Failed to fetch financial data:', err);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialData();
  }, [filters.timeframe, filters.status]);

  const handleApproveWithdrawal = async (requestId) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'approve' }));
    
    const response = await financialService.approveWithdrawal(requestId);
    
    if (response.success) {
      setWithdrawalRequests(prev => prev.filter(req => req.id !== requestId));
      alert('Withdrawal approved successfully!');
    } else {
      alert(`Failed to approve: ${response.message}`);
    }
    
    setProcessing(prev => ({ ...prev, [requestId]: false }));
  };

  const handleRejectWithdrawal = async (requestId, reason) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'reject' }));
    
    const response = await financialService.rejectWithdrawal(requestId, reason);
    
    if (response.success) {
      setWithdrawalRequests(prev => prev.filter(req => req.id !== requestId));
      alert('Withdrawal rejected successfully!');
    } else {
      alert(`Failed to reject: ${response.message}`);
    }
    
    setProcessing(prev => ({ ...prev, [requestId]: false }));
  };

  const pendingWithdrawals = withdrawalRequests.filter(req => req.status === 'pending');
  const totalPendingAmount = pendingWithdrawals.reduce((sum, req) => sum + req.amount, 0);
  const totalPendingCommission = totalPendingAmount * 0.20;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Financial Dashboard</h1>
            <p className="text-gray-600">Manage payments, commissions, and financial reports</p>
          </div>
          <div className="flex items-center space-x-2">
            <button className="btn-secondary flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export Report</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'withdrawals', name: 'Withdrawals', icon: CreditCard },
            { id: 'commissions', name: 'Commissions', icon: PieChart },
            { id: 'reports', name: 'Reports', icon: FileText }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timeframe</label>
            <select
              value={filters.timeframe}
              onChange={(e) => setFilters(prev => ({ ...prev, timeframe: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>

          {activeTab === 'withdrawals' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' && (
        <div>
          {loading ? (
            <div className="card text-center py-12">
              <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
              <p className="mt-2 text-gray-600">Loading financial data...</p>
            </div>
          ) : overview ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <FinancialCard
                title="Total Revenue"
                value={formatCurrency(overview.totalRevenue || 0)}
                subtitle="Gross platform earnings"
                icon={DollarSign}
                color="green"
              />
              
              <FinancialCard
                title="Total Commissions"
                value={formatCurrency(overview.totalCommissions || 0)}
                subtitle="20% platform commission"
                icon={TrendingUp}
                color="purple"
              />
              
              <FinancialCard
                title="Pending Payouts"
                value={formatCurrency(totalPendingAmount)}
                subtitle={`${pendingWithdrawals.length} requests`}
                icon={Clock}
                color="orange"
              />
              
              <FinancialCard
                title="Pending Commission"
                value={formatCurrency(totalPendingCommission)}
                subtitle="From pending payouts"
                icon={Shield}
                color="blue"
              />
            </div>
          ) : (
            <div className="card text-center py-12">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Financial Data</h3>
              <p className="mt-2 text-gray-500">Financial overview data is not available yet.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'withdrawals' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Withdrawal Requests</h2>
            <p className="text-gray-600">
              {pendingWithdrawals.length} pending requests • {formatCurrency(totalPendingAmount)} total • {formatCurrency(totalPendingCommission)} commission
            </p>
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="card text-center py-12">
                <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
                <p className="mt-2 text-gray-600">Loading withdrawal requests...</p>
              </div>
            ) : withdrawalRequests.length > 0 ? (
              withdrawalRequests.map((request) => (
                <WithdrawalRequestCard
                  key={request.id}
                  request={request}
                  onApprove={handleApproveWithdrawal}
                  onReject={handleRejectWithdrawal}
                  loading={processing[request.id]}
                />
              ))
            ) : (
              <div className="card text-center py-12">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No Withdrawal Requests</h3>
                <p className="mt-2 text-gray-500">
                  {filters.status !== 'pending' 
                    ? `No ${filters.status} withdrawal requests found.` 
                    : 'No pending withdrawal requests at the moment.'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add similar sections for commissions and reports tabs */}
      {activeTab === 'commissions' && (
        <div className="card text-center py-12">
          <PieChart className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Commission Reports</h3>
          <p className="mt-2 text-gray-500">Commission tracking and reports coming soon.</p>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Financial Reports</h3>
          <p className="mt-2 text-gray-500">Report generation and export coming soon.</p>
        </div>
      )}
    </DashboardLayout>
  );
};

export default FinancialDashboard;